import React from 'react'

import Layout from '../components/layouts/layout'
import BasicPageHeading from '../components/basic-page-heading'
import {combine} from '../styles/classname-utils'
import styles from './privacy.module.scss'

type SubSection = {
  name: string,
  id: string,
  children: React.ReactNode
}

type Section = {
  name: string
  id: string
  subSections: Array<SubSection>
}

interface MenuProps {
  sections: Array<Section>
}

const LinkOut = ({url, children}) => (
  <a target='_blank' rel='noopener noreferrer' href={url}>
    {children}
  </a>
)
const SupportEmailLink = () => (<a href='mailto:support@8thwall.com'>support@8thwall.com</a>)

const createPrivacyPolicySubSection = (): SubSection => {
  const children: React.ReactNode = (
    <>
      <p>Effective date: January 27, 2021</p>
      <p>
        We at 8th Wall know you care about how your personal information is used and
        shared, and we take your privacy seriously. Please read the following to learn more
        about our Privacy Policy ("Privacy Policy"). By using or accessing our Services
        ("Services") in any manner, you acknowledge that you accept the practices and
        policies outlined in this Privacy Policy, and you hereby consent that we will
        collect, use, and share your information in the following ways.
      </p>
      <p>
        {'Remember that your use of 8th Wall’s Services is at all times subject to the '}
        <LinkOut url='https://www.8thwall.com/terms'>Terms&nbsp;and&nbsp;Conditions</LinkOut>
        {' ("Terms and Conditions"), which references and incorporates this Privacy '}
        Policy. Any terms we use in this Privacy Policy without defining them have the
        definitions given to them in the Terms and Conditions.
      </p>
    </>
  )

  return {
    name: 'Privacy Policy',
    id: 'privacy-policy',
    children,
  }
}

const createPrivacyPolicyCoverSubSection = (): SubSection => {
  const children: React.ReactNode = (
    <>
      <p>
        This Privacy Policy covers our treatment of information that relates to or could
        be associated with an individual ("Personal Information") that we gather when you
        are accessing or using our Services, but not to the practices of companies we
        don’t own or control, or people that we don’t manage.
      </p>
      <p>
        We may gather various types of Personal Information from you, as explained in
        more detail below, and we use this Personal Information in connection with our
        Services.
      </p>
    </>
  )
  return {
    name: 'What does this Privacy Policy Cover?',
    id: 'what-does-this-privacy-policy-cover',
    children,
  }
}
const createPrivacyPolicyChangeSubSection = (): SubSection => {
  const children: React.ReactNode = (
    <>
      <p>
        We are constantly improving our Services, so we may need to change this Privacy
        Policy from time to time. We will alert you to changes by placing an updated
        version of the policy on this page, placing a notice on 8thwall.com, by sending
        you an email, and/or by some other means.
      </p>
      <p>
        Please note that if you’ve opted not to receive legal notice emails from us (or
        you haven’t provided us with your email address), those legal notices will still
        govern your use of our Services, and you are still responsible for reading and
        understanding them. If you use our Services after any changes to the Privacy
        Policy have been posted, this means you agree to all of the changes.
      </p>
    </>
  )
  return {
    name: 'Will 8th Wall ever change this Privacy Policy?',
    id: 'will-8th-wall-ever-change-this-privacy-policy',
    children,
  }
}
const createAbout8thWallSubSection = (): SubSection => {
  const children: React.ReactNode = (
    <>
      <p>
        8th Wall is on a mission to make augmented reality for everyone and we are doing
        this by harnessing the power and freedom of the web. We equip developers,
        agencies and brands with powerful tools to create extraordinary reality content
        that can be accessed by users in the browser.
      </p>
      <p>Our Services include:</p>
      <ul>
        <li>
          Platform Tools like the 8th Wall Cloud Editor which Developers use to create,
          collaborate and publish 8th Wall hosted WebAR and WebVR experiences using 8th
          Wall Hosting Services. This includes accessing and cloning code made available
          via templates and public-facing projects.
        </li>
        <li>
          SDKs like our AR Engine which developers use to create Self-Hosted and
          app-based experiences.
        </li>
        <li>
          APIs that are integrated into Developer projects and platforms.
        </li>
        <li>
          8th Wall developed AR and VR experiences like those found in our Project
          Library or those used for marketing and support purposes which Developers use
          as a resource to learn and develop from and Visitors use to experience AR and
          VR.
        </li>
        <li>
          Marketing and Communications like our 8thWall.com website which Visitors use to learn more
          about WebAR, WebVR and 8th Wall, our newsletter and other email communication and
          documentation used to provide marketing and support of our Services. These marketing and
          communications may feature First-Party and Third-Party content.
        </li>
      </ul>
      <p>You can use our Services in the following ways to manage your Privacy:</p>
      <ol>
        <li>
          Signing up for an 8th Wall account which allows you to create, collaborate and
          publish AR and VR content using our Platform Tools, SDKs and APIs.
        </li>
        <li>
          Accessing, viewing, cloning and interacting with AR and VR code and content
          created by Developers using our Services.
        </li>
        <li>
          Accessing, viewing, cloning and interacting with AR and VR code and content
          created by 8th Wall using our Services.
        </li>
        <li>
          Accessing and viewing our websites and receiving and responding to emails and
          other marketing and communications such as our newsletters.
        </li>
      </ol>
      <p>
        {'If you have any questions about this Privacy Policy, you can contact us at '}
        <SupportEmailLink />
        .
      </p>
    </>
  )
  return {
    name: 'About 8th Wall',
    id: 'about-8th-wall',
    children,
  }
}
const createKeyTermsSubSection = (): SubSection => {
  const children: React.ReactNode = (
    <>
      <p>
        <b>"Services"</b> has the meaning as defined above.
      </p>
      <p>
        <b>"First-Party Content"</b> means any app or website content created, managed and
        hosted by 8th Wall. This includes our marketing site, marketing and support
        communications and website content developed internally using our Services and
        our Hosting Services.
      </p>
      <p>
        <b>"Third-Party Content"</b> means any app or website content created and managed by a
        Developer using our Services. This includes content on our Hosting Services as
        well as content which is Self-Hosted. This also includes Third-Party content that
        is distributed in First-Party Content, and on an Ad Platform.
      </p>
      <p>
        <b>"Hosting Services"</b> means the components of our Services that enable Developers to
        create and publish Third-Party content hosted on 8th Wall servers.
      </p>
      <p>
        <b>"Self Hosted"</b> means the components of our Services that enables Developers to
        create and publish Third-Party content hosted on their own servers.
      </p>
      <p>
        <b>"Developer"</b> means any person or entity that is registered with us to use the
        Services.
      </p>
      <p>
        <b>"End User"</b> means a person that accesses, views and/or interacts with Third-Party
        Content created by a Developer that makes use of our Services, whether it be
        Self-Hosted or using our Hosting Services.
      </p>
      <p>
        <b>"Visitor"</b> means, depending on the context, any person who accesses, views and/or
        interacts with our First-Party Content as well as a person who visits our
        offices, or otherwise engages with us at our events or in connection with our
        marketing, support and recruitment activities.
      </p>
      <p>
        <b>"Personal Information"</b> means information that relates to or could be associated
        with an individual.
      </p>
      <p>
        <b>"You"</b> and <b>"Your"</b> means, depending on the context, either a Developer, an End
        User, or a Visitor.
      </p>
    </>
  )
  return {
    name: 'Privacy Policy Key Terms',
    id: 'privacy-policy-key-terms',
    children,
  }
}
const createEndUsersPrivacySubSection = (): SubSection => {
  const children: React.ReactNode = (
    <>
      <p>
        When you access, view and/or interact with Third-Party
        Content created with our Services, 8th Wall doesn't attempt to process information
        about who you are, but does process device and application information such as an
        IP address, which is considered Personal Information in some jurisdictions such as
        the European Economic Area.
      </p>
      <p>
        From an End User, we collect information about the device and application you use
        to access the content, such as your IP address, your operating system, your browser
        user agent, and information related to your use of the site including the length of
        time spent on the site and an anonymized session identifier that cannot be used to
        track users across different Third-Party Content or First-Party Content. IP
        addresses are only collected in standard raw web server logs for system/health
        monitoring purposes, and are only retained for 60 days.
      </p>
      <p>
        8th Wall generally operates as a processor for Third-Party Content, meaning we
        process related Personal Information pursuant to our agreements with Developers.
        We may use the device and application information that we collect to improve our
        Services. Any Developer that uses our Services to create Third-Party Content using
        our Hosting Services or Self-Hosted may be collecting additional Personal
        Information for their own products or services when you visit their site. 8th Wall
        is not in control of additional data that may be collected by a Developer for this
        content. When accessing, viewing and/or interacting Third-Party Content you should
        consider the privacy practices and policies of each Developer carefully.
      </p>
      <p>
        When First-Party or Third-Party Content is linked from or embedded within an Ad
        Platform, or when First-Party or Third-Party Content contains online
        advertisements, additional data may be collected by the Ad Platform. When viewing
        an advertisement you should consider the privacy practices and policies of each Ad
        Platform carefully.
      </p>
    </>
  )
  return {
    name: 'Privacy for End Users',
    id: 'privacy-for-end-users',
    children,
  }
}
const createVisitorsPrivacySubSection = (): SubSection => {
  const children: React.ReactNode = (
    <>
      <p>
        Whenever you access, visit and/or interact with our First-Party Content, such as
        our marketing website, contact 8th Wall via email, subscribe to our mailing list or
        respond to our surveys, we automatically receive and record information on our
        server from your browser or device, which may include your IP address, geolocation
        data, device identification, "cookie" information, the type of browser and/or
        device you’re using and the page you requested.
      </p>
      <p>
        We may use this data to customize content for you that we think you might like,
        based on your usage patterns. We may also use it to improve our Services – for
        example, this data can tell us how often users use a particular page on our
        marketing website and we can use that knowledge to make improvements to our website
        and to make the content on the website more interesting to as many users as
        possible.
      </p>
      <p>
        If you click on a link to external sites or services from our First-Party Content,
        a third-party may also transmit cookies to you. This Privacy Policy does not cover
        the use of cookies by any third parties, and we aren’t responsible for their
        privacy policies and practices.
      </p>
      <p>
        If you’ve given us your email address, we may send you 8th Wall marketing and
        promotional emails. We may receive a confirmation when you open an email from us.
        This confirmation helps improve our communications with you and makes them more
        interesting. If you do not want to receive communications from us, please indicate
        {' your preference by contacting '}
        <SupportEmailLink />
        .
      </p>
    </>
  )
  return {
    name: 'Privacy for Visitors',
    id: 'privacy-for-visitors',
    children,
  }
}
const createDevelopersPrivacySubSection = (): SubSection => {
  const children: React.ReactNode = (
    <>
      <p>
        As a Developer, by registering for an 8th Wall account, we receive and store any
        information you knowingly provide to us. For example, through the registration
        process and/or through your account settings, we may collect Personal Information
        such as your name, email address, phone number, and employer. Certain information
        may be required to register with us or to take advantage of our Services.
      </p>
      <p>
        We may communicate with you if you’ve provided us the means to do so. For example,
        if you’ve given us your email address, we may send you 8th Wall marketing and
        promotional emails, or email you about your account and use of our Services. We may
        receive a confirmation when you open an email from us. This confirmation helps
        improve our communications with you and makes them more interesting. If you do not
        want to receive communications from us, please indicate your preference by
        {' contacting '}
        <SupportEmailLink />
        .
      </p>
      <p>
        When you use our Services, we may automatically collect or receive certain
        information about your device and usage of our Services (collectively "Service
        Usage Data"). In some (but not all) countries, including countries in the European
        Economic Area ("EEA"), this information is considered Personal Information under
        applicable data protection laws. We use cookies and other tracking technologies to
        collect some of this information. For further information, please review the "Our
        Cookie Policy" section below.
      </p>
      <p>Service Usage Data may include:</p>
      <ul>
        <li>
          <b>Account Data:</b> Data you provide, manage and maintain in relation to our Services,
          such as source code, billing preferences, project metadata, and files and their
          metadata.
        </li>
        <li>
          <b>Usage Data:</b> Information about how the site is functioning as you use it, for
          identifying and fixing issues and improving the site quality of experience.
          Examples include server request logs including their latency, error status, and
          other diagnostic info including IP address, operating system and browser type,
          and client logs including info about usage of certain product features and
          whether they are functioning correctly.
        </li>
      </ul>
    </>
  )
  return {
    name: 'Privacy for Developers',
    id: 'privacy-for-developers',
    children,
  }
}
const createCookiePolicySubSection = (): SubSection => {
  const children: React.ReactNode = (
    <>
      <p>
        As a Developer or Visitor, information may automatically be collected using
        Cookies and similar technologies when you use our Services. Some of this
        information is similarly collected automatically through your browser or from your
        device. We may use this information to measure the performance of your email
        campaigns, to provide analytics information, enhance the effectiveness of our
        Service, and for other purposes described above.
      </p>
      <p>
        "Cookies" are identifiers we transfer to your browser or device that allow
        us to recognize your browser or device and tell us how and when pages are
        visited and by how many people. You may be able to change the preferences on your
        browser or device to prevent or limit your device’s acceptance of cookies, but this
        may prevent you from taking advantage of some of our Services.
      </p>
      <p>We may use cookies for our Services for the following purposes:</p>
      <p>
        <b>Essential Website Cookies:</b> These cookies are strictly necessary and help us verify
        your Account and device and determine when you’re logged in, so we can make it
        easier for you to access the Services and provide the appropriate experiences and
        features.
      </p>
      <ul>
        <li>
          Examples of cookies include 8thwall.8thwall.app and _stripe_sid, __stripe_mid.
          We use Stripe.com to process free trials and payments made through the
          First-party Site. If you wish to make an account through the First-party Site,
          you will receive an email confirmation automatically generated by stripe.com
        </li>
      </ul>
      <p>
        <b>Performance and Analytics:</b> These cookies help us analyze how the Services are being
        accessed and used, and enable us to track performance of the Services. For example,
        we use cookies to determine if you viewed a page or opened an email. This helps us
        provide you with information that you find interesting. All information these
        cookies collect is aggregated. Although important to us, these cookies are
        non-essential to the use of the 8th Wall Site. However, without these cookies,
        certain functionality may become unavailable.
      </p>
      <ul>
        <li>
          Examples of cookies include _gat, _ga, _gid. These are set by Google Universal
          Analytics. They are used to calculate visitor, session and campaign data for our
          First-Party Site analytics reports.
        </li>
      </ul>
      <p>
        <b>Advertising (Targeting) Cookies:</b> These cookies are used to make advertising
        messages more relevant to you and your interests. They also perform functions like
        preventing the same ad from continuously reappearing, ensuring that ads are
        properly displayed, and in some cases selecting advertisements that are based on
        your interests. We and our third party service providers may use these cookies to
        collect information about your browsing activities over time and across different
        websites.
      </p>
      <ul>
        <li>
          Examples of cookies NID, 1P, _JAR, _gcl_au. These cookies enable advertising and
          analytics by Google. In the future, we may add YouTube, Twitter, Facebook, and/or
          LinkedIn cookies for the purposes of enabling behavioral advertising and
          analytics by these services.
        </li>
      </ul>
      <p>
        <b>Third Party Tags:</b> We use and manage third party tags on the website and associated
        First-Party Content. Third party tags may take the form of pixels or tracking
        snippets. We use pixels to learn how you interact with our site pages and emails,
        and this information helps us and our ad partners provide you with a more tailored
        experience. We use tracking snippets to capture data from your browser, make
        requests to a third party partner or set cookies on your device to store data.
        For example, if you see an 8th Wall ad on a social media platform and choose to use
        8th Wall, we may use a tag to reduce the number of 8th Wall ads you see on that
        platform. We may use Google Tag Manager to manage our third party tag usage. This
        may cause other tags to be activated which may, for their part, collect data and
        set cookies under certain circumstances. Google Tag Manager does not store this
        data.
      </p>
      <p>
        <b>Opting Out:</b> You have the right to decide whether to accept or reject cookies.
        From your preferred browser, you can set or amend your web browser controls to
        accept or refuse cookies. If you choose to reject cookies, you may still use our
        Services although your access to some functionality and areas of First-Party
        Content may be restricted. As the means by which you can refuse cookies through
        your web browser controls vary from browser-to-browser, you should visit your
        browser’s help menu for more information.
      </p>
      <p>
        <b>Disabling Most Interest Based Advertising:</b> Most advertising networks offer you a
        way to opt out of Interest Based Advertising. If you would like to find out more
        {' information, please visit '}
        <LinkOut url='http://www.aboutads.info/choices/'>
          http://www.aboutads.info/choices
        </LinkOut>
        {' or '}
        <LinkOut url='http://www.youronlinechoices.com'>
          http://www.youronlinechoices.com
        </LinkOut>
        .
      </p>
      <p>
        We do not control cookies from Third-Party Content except those we need to use to
        properly provide the Services, such as for performance. If you click on a link to a
        third party website or service, a third party may also transmit cookies to you.
        This Privacy Policy does not cover the use of cookies by any third parties, and we
        aren’t responsible for their privacy policies and practices. Please be aware that
        cookies placed by third parties may continue to track your activities online even
        after you have left our Services, and those third parties may not honor "Do Not
        Track" requests you have set using your browser or device.
      </p>
    </>
  )
  return {
    name: 'Our Cookie Policy',
    id: 'our-cookie-policy',
    children,
  }
}
const createInformationShareSubSection = (): SubSection => {
  const children: React.ReactNode = (
    <>
      <p>We do not rent or sell your Personal Information to anyone.</p>
      <p>
        We may share your Personal Information with third parties as described in this
        section:
      </p>
      <p>
        <b>Information that’s been de-identified:</b> We may de-identify your Personal
        Information so that you are not identified as an individual. We may also provide
        aggregate usage information to our partners (or allow partners to collect that
        information from you), who may use such information to understand how often and
        in what ways people use our Services, so that they, too, can provide you with an
        optimal experience. However, we never disclose aggregate usage or de-identified
        information to a partner (or allow a partner to collect such information) in a
        manner that would identify you as an individual person.
      </p>
      <p>
        <b>Agents:</b> We employ other companies and people to perform tasks on our
        behalf and need to share your information with them to provide products or
        services to you; for example, we may use a payment processing company to receive
        and process your credit card transactions for us. Unless we tell you differently,
        our agents do not have any right to use the identifiable Personal Information we
        share with them beyond what is necessary to assist us. Note that an "agent" may
        also be considered a "partner" in certain circumstances, and would be subject to
        the terms of the "Information that’s been de-identified" section in that regard.
      </p>
      <p>
        <b>User Submissions:</b> Certain user profile information, including your name,
        employer, and any video or image content that such user has uploaded to the
        Services, may be displayed to other users to facilitate user interaction within
        the Services or address your request for our services Please remember that any
        content you upload to your public user profile, along with any Personal
        Information or content that you voluntarily disclose online in a manner other
        users can view (on discussion boards, in messages and chat areas, etc.) becomes
        publicly available, and can be collected and used by anyone. Your user name may
        also be displayed to other users if and when you send messages or comments or
        upload images or videos through the Services and other users can contact you
        through messages and comments.
      </p>
      <p>
        <b>Business Transfers:</b> We may choose to buy or sell assets, and may share
        and/or transfer Personal Information in connection with the evaluation of and
        entry into such transactions. Also, if we (or our assets) are acquired, or if we
        go out of business, enter bankruptcy, or go through some other change of control,
        Personal Information could be one of the assets transferred to or acquired by a
        third party.
      </p>
      <p>
        <b>Protection of 8th Wall and Others:</b> We reserve the right to access, read,
        preserve, and disclose any information that we believe is necessary to comply
        with law or court order; enforce or apply our Terms and Conditions and other
        agreements; or protect the rights, property, or safety of 8th Wall, our
        employees, our users, or others.
      </p>
    </>
  )
  return {
    name: 'How We Share Information',
    id: 'how-we-share-information',
    children,
  }
}
const createInformationChoicesSubSection = (): SubSection => {
  const children: React.ReactNode = (
    <>
      <p>
        For Developers using our Services, you may access, and, in some cases, edit or
        delete the following information you’ve provided to us in your account settings:
      </p>
      <ul>
        <li>Name</li>
        <li>Password</li>
        <li>Email address</li>
      </ul>
      <p>
        The information you can view, update, and delete may change as the Services
        change. If you have any questions about viewing or updating information we have
        {' on file about you, please contact us at '}
        <SupportEmailLink />
        .
      </p>
      <p>
        You can always opt not to disclose information to us, but keep in mind some
        information may be needed to register with us or to take advantage of some of our
        Services. For example, some communications are considered transactional and
        necessary for account management and you cannot opt out of these messages unless
        you cancel your 8th Wall account.
      </p>
      <p>
        You may be able to add, update, or delete information as explained above.
        When you update information, however, we may maintain a copy of the unrevised
        information in our records. You may request deletion of your account by
        {' contacting '}
        <SupportEmailLink />
        . Some information may remain in our records after you delete your account. We
        may use any aggregated data derived from or incorporate your Personal Information
        after you update or delete it, but not in a manner that would identify you
        personally.
      </p>
      <p>
        For Developers and Visitors, certain State Laws require us to indicate whether we
        honor "Do Not Track" settings in your browser. 8th Wall adheres to the standards
        set out in this Privacy Policy and does not monitor or follow any Do Not Track
        browser requests.
      </p>
      <p>
        We do not knowingly collect, solicit, or use Personal Information in any manner
        that would allow us to identify, contact, or profile anyone under the age of 13.
        If you are under 13, please do not attempt to register for our Services or send
        any Personal Information about yourself to us. If we learn that we have collected
        Personal Information from a child under age 13, we will delete that information
        as quickly as possible. If you believe that a child under 13 may have provided
        {' us with this information, please contact us at '}
        <SupportEmailLink />
        .
      </p>
    </>
  )
  return {
    name: 'Your Personal Information Choices and Opt-Outs',
    id: 'your-personal-information-choices-and-opt-outs',
    children,
  }
}
const createInformationSecuritySubSection = (): SubSection => {
  const children: React.ReactNode = (
    <>
      <p>
        As a Developer, your account is protected by a password for your privacy and
        security. You must prevent unauthorized access to your account and Personal
        Information by selecting and protecting your password and appropriately and
        limiting access to your computer or device and browser by signing off after
        you have finished accessing your account.
      </p>
      <p>
        We strive to protect the privacy of your account and other Personal Information
        we hold in our records but cannot guarantee complete security. Unauthorized entry
        or use, hardware or software failure, and other factors, may compromise the
        security of user information at any time.
      </p>
      <p>
        {'You can read about 8th Wall’s security practices '}
        <LinkOut url='https://www.8thwall.com/toms'>
          here
        </LinkOut>
        .
      </p>
    </>
  )
  return {
    name: 'Your Personal Information Security',
    id: 'your-personal-information-security',
    children,
  }
}
const createDataRetentionSubSection = (): SubSection => {
  const children: React.ReactNode = (
    <>
      <p>
        We retain Personal Information when we have an ongoing legitimate business or
        legal need to do so.
      </p>
      <p>
        The period of retention will vary depending on the type of Personal Information
        involved but generally this decision is shaped by the following criteria:
      </p>
      <ul>
        <li>If the data is necessary to provide our Service.</li>
        <li>If we have a legal or contractual need to retain the data.</li>
        <li>
          If Developers have the ability to access, edit and delete the data within their
          8th Wall account.
        </li>
        <li>
          If Developers would reasonably expect that we would retain the data until they
          remove it or until their 8th Wall account is closed or terminated.
        </li>
      </ul>
      <p>
        We retain IP addresses and device data collected from End Users using
        First-Party or Third-Party Content created using our Services for 60 days.
      </p>
    </>
  )
  return {
    name: 'Retention of Data',
    id: 'retention-of-data',
    children,
  }
}
const createLegalBasisSubSection = (): SubSection => {
  const children: React.ReactNode = (
    <>
      <p>
        For those located in the EEA or UK only, the collection of Personal Information
        as described above will depend on the information and the context in which we
        collect it.
      </p>
      <p>
        We will typically collect and use Personal Information from you where the
        processing of it is in our legitimate interests and not overridden by your
        data-protection interests or fundamental rights and freedoms. Our legitimate
        interests are described in more detail in this privacy policy above, but they
        typically include optimizing the experience in your use of our Services and
        supporting our marketing activities.
      </p>
      <p>
        If you are a Developer, we will need Personal Information to grant you access to
        our Services. In some cases, we may also have a legal obligation to collect
        Personal Information from you. If we ask you to provide Personal Information
        to comply with a legal requirement or to create an account with us, we will make
        this clear at the time and advise you whether the collection of this information
        is mandatory or not, as well as of the possible consequences if you do not
        provide your Personal Information.
      </p>
      <p>
        If you are a Visitor, whenever you access, visit and/or interact with our
        First-Party Content, such as our marketing website, contact 8th Wall via email,
        subscribe to our mailing list or respond to our surveys, we automatically receive
        and record information on our server from your browser or device, which may
        include your IP address, geolocation data, device identification, "cookie"
        information, the type of browser and/or device you’re using and the page you
        requested. If you click on a link to external sites or services from our
        First-Party Content, a third-party may also transmit cookies to you. This
        Privacy Policy does not cover the use of cookies by any third parties, and
        we aren’t responsible for their privacy policies and practices.
      </p>
      <p>
        Where required by law, we will collect Personal Information only where we have
        your consent to do so. If you have questions or need further information
        concerning the legal basis on which we collect and use your Personal Information,
        {' please contact us at '}
        <SupportEmailLink />
        .
      </p>
    </>
  )
  return {
    name: 'Legal Basis for Processing Personal Information (EEA and UK Persons Only)',
    id: 'legal-basis-for-processing-personal-information-eea-and-uk-persons-only',
    children,
  }
}
const createGDPRSubSection = (): SubSection => {
  const children: React.ReactNode = (
    <>
      <p>
        Subject to the applicable provision of the GDPR, you have the following rights
        with respect to your Personal Information:
      </p>
      <ul>
        <li>
          If we rely on your consent to process your Personal Information, to withdraw
          that consent at any time, but this will not affect any processing of your
          Personal Information that has already taken place before withdrawal of
          your consent.
        </li>
        <li>
          To access your Personal Information (commonly known as a "data subject access
          request") along with certain other details in relation to its processing (if
          you require additional copies, we may need to charge a reasonable fee).
        </li>
        <li>
          To rectify your Personal Information if it is inaccurate or incomplete.
        </li>
        <li>
          To have your Personal Information erased if it is no longer necessary for the
          purposes for which it was processed; you have withdrawn your consent where
          previously given and there is no other legal ground for processing; you object
          to its processing for direct marketing purposes; or you object to the
          processing of your data based on a legitimate interest and there is no other
          overriding legitimate grounds for processing; or you consider it to have been
          unlawfully processed.

        </li>
        <li>
          To have the processing of your Personal Information restricted in certain
          circumstances, such as if you contest its accuracy or object to us processing
          it.
        </li>
        <li>
          To have your Personal Information transferred to another company in a
          structured, commonly used and machine-readable format, if you consented to give
          your Personal Information to us or it was provided to us as necessary in
          connection with our contract with you and if the processing is carried out by
          automated means.
        </li>
        <li>
          To object to the processing of your Personal Information, and we will cease
          processing it if (a) we are relying on a legitimate interest to process your
          Personal Information, unless we demonstrate compelling legitimate grounds for
          the processing or (b) we are processing your Personal Information for direct
          marketing.
        </li>
        <li>
          To lodge a complaint with your national data protection authority if you have a
          concern about our privacy practices, including the way we handle your Personal
          Information.
        </li>
      </ul>
      <p>
        If you would like to submit a request to us to exercise your rights, please
        {' contact us at '}
        <SupportEmailLink />
        {'.  Please note that you must verify your '}
        identity before we will process your request.  You may be required to provide
        email confirmation or other information in order for us to verify your identity.
      </p>
    </>
  )
  return {
    name: 'Your Rights in Relation to Your Personal Information under GDPR',
    id: 'your-rights-in-relation-to-your-personal-information-under-gdpr',
    children,
  }
}
const createInternationalTransfersSubSection = (): SubSection => {
  const children: React.ReactNode = (
    <>
      <p>
        8th Wall operates in the United States. Our servers and offices are located in
        the United States, so your information may be transferred to, stored, or
        processed in the United States. While the data protection, privacy, and other
        laws of the United States might not be as comprehensive as those in your country,
        we take many steps to protect your privacy, including offering our Developers a
        {' Data Processing Agreement available '}
        <LinkOut url='https://www.8thwall.com/dpa'>here</LinkOut>
        .
      </p>
      <p>
        If you have any concerns about your data protection as it relates to the country
        {' you are doing business, please contact us at '}
        <SupportEmailLink />
        .
      </p>
    </>
  )
  return {
    name: 'International Transfers',
    id: 'international-transfers',
    children,
  }
}
const createQuestionsSubSection = (): SubSection => {
  const children: React.ReactNode = (
    <p>
      If you have any questions or concerns regarding our Privacy Policy, please send
      {' us a detailed message to '}
      <SupportEmailLink />
      , and we will try to resolve your concerns.
    </p>
  )
  return {
    name: 'What if I have questions about this Policy?',
    id: 'what-if-i-have-questions-about-this-policy',
    children,
  }
}


const SECTIONS: Array<Section> = [
  {
    name: 'Overview',
    id: 'overview',
    subSections: [
      createPrivacyPolicySubSection(),
      createPrivacyPolicyCoverSubSection(),
      createPrivacyPolicyChangeSubSection(),
      createAbout8thWallSubSection(),
      createKeyTermsSubSection(),
    ],
  },
  {
    name: 'User Privacy',
    id: 'user-privacy',
    subSections: [
      createEndUsersPrivacySubSection(),
      createVisitorsPrivacySubSection(),
      createDevelopersPrivacySubSection(),
    ],
  },
  {
    name: 'Cookies',
    id: 'cookies',
    subSections: [
      createCookiePolicySubSection(),
    ],
  },
  {
    name: 'General Information',
    id: 'general-information',
    subSections: [
      createInformationShareSubSection(),
      createInformationChoicesSubSection(),
      createInformationSecuritySubSection(),
      createDataRetentionSubSection(),
      createLegalBasisSubSection(),
      createGDPRSubSection(),
      createInternationalTransfersSubSection(),
      createQuestionsSubSection(),
    ],
  },
]

const SectionsMenu : React.FC<MenuProps> = ({sections}) => {
  const [menuOpen, setMenuOpen] = React.useState(false)
  const [activeSectionId, setActiveSectionId] = React.useState(sections[0].id)
  const menuClasses = `${styles.privacySectionsMenu}${menuOpen ? ` ${styles.open}` : ''}`

  React.useEffect(() => {
    const handleScrollEvent = () => {
      const sectionIds = sections.map(s => s.id)
      let activeSectionId = sectionIds[0]
      sectionIds.forEach((id) => {
        const section = document.getElementById(id)
        if (!section) {
          return
        }

        const scrolledPastTop = section.offsetTop <= window.scrollY
        const scrolledPastBottom = (
          (section.offsetTop + section.offsetHeight) <= window.scrollY
        )
        if (scrolledPastTop && !scrolledPastBottom) {
          activeSectionId = id
        }
      })

      setActiveSectionId(activeSectionId)
    }

    document.addEventListener('scroll', handleScrollEvent)
    return () => {
      document.removeEventListener('scroll', handleScrollEvent)
    }
  }, [])

  React.useEffect(() => {
    const anchorId = window.location.hash
    if (!anchorId) {
      return
    }

    const element = document.getElementById(anchorId.substring(1))
    if (element) {
      element.scrollIntoView()
    }
  }, [])

  return (
    <div className={styles.privacySectionsContainer}>
      <div className={menuClasses}>
        {/* TODO(alvin): Make this keyboard accessible. */}
        <header onClick={() => { setMenuOpen(!menuOpen) }}>
          In this document
        </header>
        <ul className={styles.sectionsItems}>
          {sections.map(({name, id}, index) => (
            <li key={id}>
              <a href={`#${id}`} className={id === activeSectionId ? styles.active : ''}>
                {`${index + 1}. ${name}`}
              </a>
            </li>
          ))}
        </ul>
      </div>
    </div>
  )
}

// TODO(alvin): Add a description to the Layout component for this page's
// metadata.
export default () => (
  <Layout title='Privacy Policy'>
    <BasicPageHeading>Privacy Policy</BasicPageHeading>
    <section className={combine('light', styles.privacyPage)}>
      <div className='d-flex flex-column'>
        <div className={combine('max-width', styles.privacyPage)}>
          <SectionsMenu sections={SECTIONS} />
          <div className={combine('justify-content-center', styles.privacyMain)}>
            <div>
              {SECTIONS.map((section, i) => (
                <section
                  id={section.id}
                  key={section.id}
                  className={combine(styles.privacySection, 'pt-2', 'pb-0', 'text8-md')}
                >
                  {i !== 0 && <hr />}
                  <h2 className={styles.privacySectionName}>
                    {section.name}
                  </h2>
                  {section.subSections.map(subSection => (
                    <React.Fragment key={subSection.id}>
                      <h3 id={subSection.id} className='font8-black'>
                        {subSection.name}
                      </h3>
                      {subSection.children}
                    </React.Fragment>
                  ))}
                </section>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  </Layout>
)
